import { FC, ReactElement, memo, useCallback } from 'react';
import { useIsFetching, useIsMutating, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';

import { AppMessage } from '@/common/components';
import { AppRoutes } from '@/common/interfaces';
import { FallbackedLayout } from '@/common/layouts';
import { messageOnError } from '@/common/utils';

import { ReferralLinksQueryKeys, ReferralsQueryKeys, useDeleteReferralsMutation, useGetByIdReferralsQuery } from '@/modules/Sites';

import { ReferralsOverviewView } from './ReferralsOverviewView';

interface Props {
  siteId: string;
  referralId: string;
}

export const ReferralsOverviewContainer: FC<Props> = memo(({ siteId, referralId }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const appMessageApi = AppMessage.useControls();

  const {
    data: referralData,
    error: referralError,
    isLoading: isReferralLoading,
    isError: isReferralError
  } = useGetByIdReferralsQuery(siteId, referralId)({
    enabled: !!referralId && !!siteId
  });

  const { isLoading: isDeleteReferralLoading, mutate: deleteHostingMutation } =
  useDeleteReferralsMutation(siteId, referralId)({
    onSuccess: async () => {
      await queryClient.refetchQueries([ReferralsQueryKeys.GetAll, siteId]);

      appMessageApi.success('Реферал успішно видалено!');

      navigate(AppRoutes.Referrals.replace(':siteId', siteId));
    },
    onError: (error) => {
      messageOnError(error, appMessageApi);
    }
  });

  const isReferralLinksListFetching = !!useIsFetching({
    queryKey: [ReferralLinksQueryKeys.GetAll, siteId, referralId],
    exact: true
  });

  const isReferralLinksCreating = !!useIsMutating({
    mutationKey: [ReferralLinksQueryKeys.Create, siteId, referralId],
    exact: true
  });

  const isReferralLinksEditing = !!useIsMutating({
    mutationKey: [ReferralLinksQueryKeys.Edit, siteId, referralId],
    exact: false
  });

  const isReferralLinksDeleting = !!useIsMutating({
    mutationKey: [ReferralLinksQueryKeys.Delete, siteId, referralId],
    exact: true
  });

  const handleDeleteReferral = useCallback((): void => {
    deleteHostingMutation();
  }, []);

  const handleEditReferral = useCallback((): void => {
    navigate(AppRoutes.ReferralsEdit.replace(':siteId', siteId).replace(':referralId', referralId));
  }, [siteId, referralId]);

  return (
    <FallbackedLayout
      data={ referralData }
      isLoading={ isReferralLoading }
      isError={ isReferralError }
      error={ referralError }
    >
      { (referral): ReactElement => (
        <ReferralsOverviewView
          referral={ referral }
          disableButtons={
            isReferralLinksCreating ||
            isReferralLinksEditing ||
            isReferralLinksDeleting ||
            isDeleteReferralLoading ||
            isReferralLinksListFetching
          }
          isDeleteReferralLoading={ isDeleteReferralLoading }
          handleEditReferral={ handleEditReferral }
          handleDeleteReferral={ handleDeleteReferral }
        />
      ) }
    </FallbackedLayout>
  );
});
